import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchFacets } from '@app/src/api/fetchHooks'
import { ASSESSMENT_TEMPLATE_ID_PROP, TOTAL_LEVEL_PROP } from '@app/src/hooks/getAssessmentFilterLabel'
import { useGetFilterLabels } from '@app/src/hooks/getFilterLabels'
import useActiveRiskIndexByType from '@app/src/hooks/useActiveRiskIndexByType'
import { useAssessmentTemplatesWithSections } from '@app/src/hooks/useAssessmentTemplatesWithSections'
import { useRiskStatus } from '@app/src/hooks/useRiskStatus'
import {
  getAutomationSummaryLabel,
  getCompleteAutomationSummary,
  getSectionLevels,
  getTotalLevelLabel,
} from '@app/src/pages/Configurations/ConfigurationsPages/Automation/automationUtils'
import { extractFilterData } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/components/FilterDataExtractor'
import { useRiskFilterProcessor } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/components/RiskFilterProcessor'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import type { FilterGroup, FilterValue } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { RequestAutomation } from '@app/src/types/automations'
import type { User } from '@app/src/types/resourceExplorer'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import type { ReactElement, ReactNode } from 'react'
import { Children, isValidElement, useMemo } from 'react'
import { useIntl } from 'react-intl'

const filterLabelToText = (label: ReactNode): string => {
  if (typeof label === 'string' || typeof label === 'number') {
    return String(label)
  }

  if (Array.isArray(label)) {
    return label.map(filterLabelToText).join('')
  }

  if (isValidElement(label)) {
    const children = Children.toArray((label as ReactElement).props.children)
    return children.map(filterLabelToText).join('')
  }

  return ''
}

const buildAssessmentCategoryText = (
  filters: FilterGroup[],
  values: string[],
  formatMessage: (msg: { id: string }, values?: { count: number }) => string,
  getFilterLabel: (property: string, value: FilterValue | undefined, operator?: Operators) => ReactNode,
): string => {
  if (!filters?.length || !values?.length || !filters[0]?.name || !values[0]) return ''

  const andJoiner = formatMessage({ id: 'automatedSendout.automationSpecifications.andJoiner' })
  const labelNode = getFilterLabel(filters[0].name, [values[0]])
  const firstCategory = filterLabelToText(labelNode)
  const moreText =
    values.length > 1 ? ` ${formatMessage({ id: 'automatedSendout.plusMore' }, { count: values.length - 1 })}` : ''

  return `${andJoiner}${firstCategory}${moreText}`
}

type AutomationSummaryData = {
  automationType?: RequestAutomationType
  summaryLabel: string
  templateName?: string
  totalLevelLabel?: string
  sectionLevels: Array<{ sectionName: string; level: string }>
  riskFilters: Array<{ type: string; indexLabel: string; riskLabel: string }>
  assessmentCategoryText: string
  sourceFilterValue?: string
  categoryLabel?: string
  additionalCategoryCount: number
  additionalCategoriesTooltipLabels: ReactNode[]
  categoryOptionsFilterValue: string[]
  isCategoryLabelsLoading: boolean
}

const useAutomationSummaryData = (automation: RequestAutomation | undefined): AutomationSummaryData => {
  const intl = useIntl()
  const { formatMessage } = intl
  const { assessmentTemplates } = useAssessmentTemplatesWithSections()
  const { riskStatusOptions } = useRiskStatus()
  const { countryOptions, industryOptions } = useActiveRiskIndexByType()

  const categoryFilters = automation?.categoryFilters
  const automationType = automation?.automationType

  const {
    standardCategoriesFilter,
    categoryOptionsFilterValue,
    assessmentCategoryFilters,
    assessmentCategoryValues,
    countryFilterValue,
    actorTypeIdFilterValue,
    allCategoriesValues,
    sourceFilterValue,
  } = extractFilterData(categoryFilters)

  const { riskFilters } = useRiskFilterProcessor(categoryFilters, riskStatusOptions, countryOptions, industryOptions)

  const providerOwnerFilterValue =
    (categoryFilters?.find(cf => cf.name === 'providerOwners.userId')?.filters?.[0]?.value as string[]) ?? []

  const {
    facets: [countryNames],
    isLoading: isCountryNamesLoading,
  } = useFetchFacets({
    key: FetchKey.CountryFacets,
    endpoint: endpoints.countriesWithFacets,
    facetsParam: [{ name: 'iso3166Alpha2' }],
    options: {
      enabled: Boolean(countryFilterValue.length),
    },
    filter: [
      {
        name: 'id',
        filters: [
          {
            value: countryFilterValue,
            operator: Operators.In,
          },
        ],
      },
    ],
  })

  const {
    facets: [actorTypeNames],
    isLoading: isActorTypesLoading,
  } = useFetchFacets({
    key: FetchKey.ActorTypeFacets,
    endpoint: endpoints.ActorTypesWithFacets,
    facetsParam: [{ name: 'name' }],
    options: {
      enabled: Boolean(actorTypeIdFilterValue.length),
    },
    filter: [
      {
        name: 'id',
        filters: [
          {
            value: actorTypeIdFilterValue,
            operator: Operators.In,
          },
        ],
      },
    ],
  })

  const { data: userNamesData, isLoading: isUserNamesLoading } = useFetchCollectionWithPost<User>({
    key: FetchKey.Colleague,
    endpoint: endpoints.colleagues,
    payload: {
      pagination: {
        itemsPerPage: 9999,
        pageNumber: 1,
      },
      include: [],
      filter: [
        {
          name: 'id',
          filters: [
            {
              value: providerOwnerFilterValue,
              operator: Operators.In,
            },
          ],
        },
      ],
    },
    options: {
      enabled: Boolean(providerOwnerFilterValue.length),
      staleTime: ONE_MINUTE_MS,
    },
  })

  const userNames = useMemo(
    () => userNamesData?.items.map(user => ({ label: user.name, value: String(user.id), count: 0 })) ?? [],
    [userNamesData],
  )

  const { getFilterLabel } = useGetFilterLabels({ countryNames, actorTypeNames, userNames })

  const templateId = categoryFilters?.find(f => f.name === ASSESSMENT_TEMPLATE_ID_PROP)?.filters?.[0]?.value
  const template = assessmentTemplates?.find(t => t.id === Number(templateId))

  const totalLevel = categoryFilters?.find(f => f.name === TOTAL_LEVEL_PROP)?.filters?.[0]?.value
  const totalLevelLabel = template ? getTotalLevelLabel(totalLevel, template, intl.formatMessage) : undefined

  const sectionLevels =
    template && categoryFilters ? getSectionLevels(categoryFilters, template, intl.formatMessage) : []

  const assessmentCategoryText = assessmentCategoryFilters
    ? buildAssessmentCategoryText(
        assessmentCategoryFilters,
        assessmentCategoryValues.map(v => String(v)),
        formatMessage,
        getFilterLabel,
      )
    : ''

  const categoryLabel = useMemo(() => {
    if (automationType !== RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY || !categoryFilters?.length) {
      return undefined
    }

    const firstFilterName = categoryFilters[0]?.name
    const firstValue = allCategoriesValues[0]

    if (!firstFilterName || firstValue === undefined || firstValue === null) {
      return undefined
    }

    const labelNode = getFilterLabel(firstFilterName, [String(firstValue)])
    const label = filterLabelToText(labelNode)

    return label || undefined
  }, [automationType, categoryFilters, allCategoriesValues, getFilterLabel])

  const additionalCategoriesTooltipLabels = useMemo(() => {
    if (automationType !== RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY) {
      return []
    }

    return standardCategoriesFilter
      ?.map(cf => (cf.filters?.[0]?.value ? getFilterLabel(cf.name, cf.filters[0].value as string[]) : null))
      .filter((label): label is ReactNode => Boolean(label))
  }, [automationType, standardCategoriesFilter, getFilterLabel])

  const summaryLabel = !automationType
    ? ''
    : automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY && categoryLabel
      ? categoryLabel
      : getAutomationSummaryLabel(
          automationType,
          intl,
          template?.name,
          totalLevelLabel,
          sectionLevels,
          riskFilters,
          assessmentCategoryText,
          sourceFilterValue || undefined,
        )

  return {
    automationType,
    summaryLabel,
    templateName: template?.name,
    totalLevelLabel,
    sectionLevels,
    riskFilters,
    assessmentCategoryText,
    sourceFilterValue: sourceFilterValue || undefined,
    categoryLabel,
    additionalCategoryCount: Math.max(allCategoriesValues.length - 1, 0),
    additionalCategoriesTooltipLabels,
    categoryOptionsFilterValue: categoryOptionsFilterValue.map(String),
    isCategoryLabelsLoading: isCountryNamesLoading || isActorTypesLoading || isUserNamesLoading,
  }
}

export function useAutomationSummary(automation: RequestAutomation | undefined): string {
  return useAutomationSummaryData(automation).summaryLabel
}

export function useCompleteAutomationSummary(automation: RequestAutomation | undefined): string {
  const intl = useIntl()
  const data = useAutomationSummaryData(automation)

  if (!data.automationType) {
    return ''
  }

  const baseSummary = getCompleteAutomationSummary(
    data.automationType,
    intl,
    data.templateName,
    data.totalLevelLabel,
    data.sectionLevels,
    data.riskFilters,
    data.assessmentCategoryText,
    data.sourceFilterValue,
    data.categoryLabel,
  )

  // For ProviderInCategory automation with multiple filters, add "+ X more" text
  if (data.automationType === RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY && data.additionalCategoryCount > 0) {
    const action = intl.formatMessage({ id: 'automatedSendout.sendQuestionnaire' })
    const moreText = intl.formatMessage({ id: 'automatedSendout.plusMore' }, { count: data.additionalCategoryCount })
    return data.categoryLabel ? `${data.categoryLabel} ${moreText} → ${action}` : `${moreText} → ${action}`
  }

  return baseSummary
}

export function useAutomationDisplayData(automation: RequestAutomation | undefined) {
  return useAutomationSummaryData(automation)
}
